import { Inject, Injectable, LOCALE_ID, OnDestroy } from '@angular/core';
import { Store } from '@ngrx/store';
import { Subscription } from 'rxjs';
import * as URI from 'urijs';

import { State, getCurrentUserState } from '®app/ngrx/state';
import { User } from '®shared/auth';
import { AppConfig } from '®shared/config/app-config';
import { ContainerIdentifierWithIndex } from '®shared/container-structure';
import { RequestDigitisationIdentifier } from '®shared/container-structure/edit/request-digitisation/request-digitisation-identifier';
import { UserPermissions } from '®shared/permissions';
import { ListExportUrls } from '®shared/reading-list/models/list-export-urls';

declare const window: { btoa: Function };

type ViewType = 'lists' | 'sections' | 'items';

@Injectable()
export class UriService implements OnDestroy {
  private currentUserSubscription: Subscription;
  private accessToken: string | null = null;
  private userPermissions: UserPermissions | null = null;

  private localShipshapeListUrlRegex =
    /shipshape\.talis\.local:4200\/3\/life\/lists\/([^/?#]+?)(?=\.html(?:[?#]|$)|[?#]|$)/i;

  constructor(
    private appConfig: AppConfig,
    @Inject(LOCALE_ID) private locale: string,
    private store: Store<State>,
  ) {
    this.currentUserSubscription = this.store
      .select(getCurrentUserState)
      .subscribe(state => {
        const { accessToken, loadableUser } = state;
        this.accessToken = accessToken;
        this.userPermissions =
          loadableUser && loadableUser.data
            ? loadableUser.data.permissions
            : null;
      });
  }

  ngOnDestroy() {
    if (this.currentUserSubscription) {
      this.currentUserSubscription.unsubscribe();
    }
  }

  /**
   * Constructs a link to a list given its ID
   * @param {string} listId - The ID of the list
   * @return {string} A URL to the list
   */
  getListViewUrl(listId: string): string {
    return new URI(this.appConfig.apiEndpoint)
      .segment(this.appConfig.tenant.shortCode)
      .segment(`lists/${listId}`)
      .suffix('html')
      .toString();
  }

  getListIdFromUrl(url: string): string | null {
    if (!this.isValidUrl(url)) {
      return null;
    }

    const apiEndpoint = this.appConfig.apiEndpoint;
    const vanityUrlHost = new URI(this.appConfig.tenant.conceptBaseUri).host();
    const rlHost = new URI(this.appConfig.apiEndpoint).host();
    const tenantCode = this.appConfig.tenant.shortCode;

    const vanityUrlRegex = new RegExp(
      `//${vanityUrlHost}/lists/[\\w\\d\-]+(\.?|$)`,
    );
    const tenantRlTalisRegex = new RegExp(
      `//${tenantCode}.${rlHost}/lists/[\\w\\d\-]+(\.?|$)`,
    );
    const v3Regex = new RegExp(
      `${apiEndpoint}/${tenantCode}/lists/[\\w\\d\-]+(\.?|$)`,
    );

    if (
      vanityUrlRegex.test(url) ||
      tenantRlTalisRegex.test(url) ||
      v3Regex.test(url)
    ) {
      const uri = new URI(url);
      const listId = uri.suffix()
        ? uri.filename().replace('.' + uri.suffix(), '')
        : uri.filename();
      return listId;
    } else if (this.localShipshapeListUrlRegex.test(url)) {
      // This the the edge case purely for local Shipshape development
      return this.getListIdFromLocalShishapeUrl(url);
    } else {
      return null;
    }
  }

  isValidUrl(url: string): boolean {
    try {
      new URL(url);
      return true;
    } catch (e) {
      return false;
    }
  }

  private getListIdFromLocalShishapeUrl(url: string): string | null {
    const match = url.match(this.localShipshapeListUrlRegex);
    return match ? match[1] : null;
  }

  /**
   * Constructs a link to a section given its ID
   * @param {string} sectionId - The ID of the section
   * @return {string} A URL to the section
   */
  getSectionUrl(sectionId: string): string {
    const uri = new URI(this.appConfig.apiEndpoint)
      .segment(this.appConfig.tenant.shortCode)
      .segment(`sections/${sectionId}`)
      .suffix('html');

    if (this.locale) {
      uri.search({ lang: this.locale });
    }

    return uri.toString();
  }

  /**
   * Get external link URL relative to a tenancy base URI
   * @param {string|null} externalLink - The relative external link URL
   * @return {string|undefined} An external link URL, made absolute through the TARL base URL.
   */
  getExternalLinkRelativeToBaseUri(link: string): string | undefined {
    if (!link || !link.trim()) {
      return undefined;
    }

    const baseUri = new URI(this.appConfig.tenant.httpsBaseUri);
    const externalUri = new URI(link);
    return baseUri
      .segment(externalUri.segment())
      .search(externalUri.search())
      .toString();
  }

  /**
   * Get TARL classic list URL for a reading list.
   * @param {string} listId - The ID of the list to retrieve.
   * @param {boolean} isDraft - `true` when viewing a draft list; `false` otherwise.
   * @return {string} The TARL classic URL for specified list.
   */
  getTarlListViewUrl(listId: string, isDraft: boolean): string {
    const baseParams = { version: 'v1' };
    const uri = this.getTarlViewUri('lists', listId, isDraft);
    return this.getClassicUri(uri.addSearch(baseParams)).toString();
  }

  /**
   * Get TARL classic list URL for a reading list to send to analytics.
   * @param {string} listId - The ID of the list to retrieve.
   * @param {boolean} isDraft - `true` when viewing a draft list; `false` otherwise.
   * @return {string} The TARL classic URL for analytics.
   */
  getTarlListViewUrlForAnalytics(listId: string, isDraft: boolean): string {
    return this.getTarlViewUri('lists', listId, isDraft)
      .suffix('html')
      .toString();
  }

  /**
   * Get a classic TARL URL for a reading list section
   * @param {string} sectionId - The ID of the section to retrieve.
   * @param {boolean} isDraft - `true` when viewing a draft list; `false` otherwise.
   * @return {string} The TARL classic URL for specified reading list section.
   */
  getTarlSectionViewUrl(sectionId: string, isDraft: boolean): string {
    return this.getTarlViewUri('sections', sectionId, isDraft)
      .suffix('html')
      .toString();
  }

  /**
   * Get a classic TARL URL for a reading list item
   * @param {string} itemId - The ID of the item to retrieve.
   * @param {boolean} isDraft - `true` when viewing a draft list; `false` otherwise.
   * @return {string} The TARL classic URL for specified reading list item.
   */
  getTarlItemViewUrl(itemId: string, isDraft: boolean): string {
    return this.getTarlViewUri('items', itemId, isDraft)
      .suffix('html')
      .toString();
  }

  /**
   * Get a classic TARL uri for the specified view.
   * @param {ViewType} viewType - 'section', 'item' or 'list'.
   * @param {string} id - The ID of the view to retrieve.
   * @param {boolean} isDraft - `true` when viewing a draft list; `false` otherwise.
   * @return {URI} The TARL classic URI for specified view.
   */
  getTarlViewUri(viewType: ViewType, id: string, isDraft: boolean): URI {
    const params: { draft?: string } = isDraft ? { draft: 'true' } : {};
    const base = new URI(this.appConfig.tenant.httpsBaseUri);
    return this.getClassicUri(base.segment(`${viewType}/${id}`).search(params));
  }

  /**
   * Constructs the URL to edit resource metadata in the classic interface.
   * @param {string} resourceId - The id for the resource to be edited
   * @param {string} itemId - The id for the item in question - because resources and items are different
   * @param {string} returnUrl - The current page URL, for when editing is complete.
   * @return {string} A url to the item in the classic interface
   */
  getResourceEditUrlForItem(
    resourceId: string,
    itemId: string,
    returnUrl?: string,
  ): string {
    const referrer = returnUrl ? returnUrl : new URI().toString();
    const itemUri = new URI(this.appConfig.tenant.conceptBaseUri)
      .segment(`items/${itemId}`)
      .toString();
    const editUri = new URI(this.appConfig.tenant.httpsBaseUri)
      .segment(`resources/${resourceId}`)
      .search('edit')
      .search(data => ({ ...data, referrer, itemUri }))
      .suffix('html');
    return this.getClassicUri(editUri).toString();
  }

  getResourceEditUrlForResource(
    resourceId: string,
    returnUrl?: string,
  ): string {
    const referrer = returnUrl ? returnUrl : new URI().toString();
    const editUri = new URI(this.appConfig.tenant.httpsBaseUri)
      .segment(`resources/${resourceId}`)
      .search('edit')
      .search(data => ({ ...data, referrer }))
      .suffix('html');
    return this.getClassicUri(editUri).toString();
  }

  /**
   * Constructs a URL to create a manual bookmark in the classic bookmarklet interface
   * @param {string} returnUrl - The current page URL, for when editing is complete
   * @return {string} A url to the bookmarklet in the classic interface
   */
  getCreateManualBookmarkUrl(
    returnUrl?: string,
    containerLocation?: ContainerIdentifierWithIndex,
  ): string {
    const referrer = returnUrl ? returnUrl : this.getCurrentUri().toString();
    const params = { referrer, bookmarkingMechanism: 'create_manual_bookmark' };
    if (containerLocation) {
      params['svc.containerindex'] = containerLocation.index;
      params['svc.' + containerLocation.type.slice(0, -1) + 'id'] =
        containerLocation.id;
    }

    const bookmarkletUri = new URI(this.appConfig.tenant.httpsBaseUri)
      .segment('ui/forms/bookmarklet')
      .suffix('html')
      .search(params);
    return this.getClassicUri(bookmarkletUri).toString();
  }

  /**
   * Builds a URL to TARLs 'New List' screen with pertinent fields prefilled
   * @param {string} listId - An id to a list
   * @param {boolean} isDraft - `true` for a draft list; `false` otherwise
   * @return {string} A URL
   */
  constructCopyListUrl(listId: string, isDraft = false): string {
    const listConceptUri = this.getListConceptUri(listId);
    const encodedUri = window.btoa(listConceptUri.toString());
    const copyListUri = new URI(this.appConfig.tenant.httpsBaseUri)
      .segment('ui/forms/newlist')
      .suffix('html')
      .query({
        uri: encodedUri,
        draft: isDraft ? '1' : undefined,
      });

    return this.getClassicUri(copyListUri).toString();
  }

  /**
   * Constructs a link to the user's list collection page
   * @return {string} A URL to the user's 'My Lists' page
   */
  getTarlMyListsUrl(): string {
    const myListsUri = new URI(this.appConfig.tenant.httpsBaseUri).segment(
      '/mylists',
    );
    return this.getClassicUri(myListsUri).toString();
  }

  /**
   * Builds a URL containing information TARL needs to invite new publishers to a list.
   * @param {string} listId - An id to a list
   * @return {string} The URL to the 'Send Invite' page in TARL
   */
  constructInvitePublishersUrl(listId: string): string {
    const appliesTo = this.getListConceptUri(listId).toString();
    const inviteListPublishersUri = new URI(this.appConfig.tenant.httpsBaseUri)
      .segment('roles/list-publisher')
      .suffix('html')
      .query({
        appliesTo: appliesTo,
        invite: 'true',
        returnTo: this.getCurrentUri().toString(),
      });
    return this.getClassicUri(inviteListPublishersUri).toString();
  }

  /**
   * Get TARL export URLs for a reading list, including: CSV, PDF, RefWorks, and RIS.
   * @param {string} listId - The ID of the list to retrieve
   * @param {string|null} citationStyleId - The ID of the citation style for the bibliography
   * @return {ListExportUrls} An object containing each URL
   **/
  getExportUrls(
    listId: string,
    citationStyleId: string | null,
  ): ListExportUrls {
    const base = new URI(this.appConfig.tenant.httpsBaseUri);

    const csv = base.clone().segment(`/lists/${listId}.csv`).toString();
    const docx = base.clone().segment(`/lists/${listId}.docx`).toString();
    const pdf = base.clone().segment(`/lists/${listId}.pdf`).toString();
    const ris = base.clone().segment(`/lists/${listId}.ris`).toString();
    const bibliography = !citationStyleId
      ? null
      : base
          .clone()
          .segment(`/lists/${listId}/bibliography.pdf`)
          .query({ style: citationStyleId })
          .toString();

    return { csv, docx, pdf, ris, bibliography };
  }

  /**
   * Builds the TARL RIS export URL for an item
   * @param  {string} itemId - The ID of the item
   * @return {string} The URL to an item's RIS file
   */
  getItemExportUrl(itemId: string): string {
    return new URI(this.appConfig.tenant.httpsBaseUri)
      .segment(`/items/${itemId}.ris`)
      .toString();
  }

  getResourceExportUrl(itemId: string): string {
    return new URI(this.appConfig.tenant.httpsBaseUri)
      .segment(`/resources/${itemId}.ris`)
      .toString();
  }

  /**
   * Returns an RL request digitisation link. This is a launch URL that will
   * return a DC form for use in an iFrame.
   * @param {string} itemId - The ID of the item the digitisation is being requested for
   * @param {string} user - The access token to use to authenticate the request
   * @param {RequestDigitisationIdentifier} identifier - Optional ISBN or DOI to use in the request.
   */
  getRequestDigitisationLink(
    itemId: string,
    user: User,
    identifier?: RequestDigitisationIdentifier,
  ): string {
    const requestDigitisationLaunchURI = new URI(this.appConfig.apiEndpoint);
    const query = {
      origin: new URI().origin(),
      access_token: this.accessToken,
      guid: user.guid,
    };

    if (identifier) {
      query[identifier.type] = identifier.value;
    }

    return requestDigitisationLaunchURI
      .segment(
        `/${this.appConfig.tenant.shortCode}/digitisation_request/launch/${itemId}`,
      )
      .query(query)
      .toString();
  }

  /**
   * Constructs a link to an individual item page, for use in the share sheet
   * @param {string} baseHref - Where the application is being served from
   * @param {string} shortCode - The tenancy short code
   * @param {string} itemId - The id for the item in question
   * @return {string} A url to a single item view
   */
  getItemShareUrl(baseHref: string, shortCode: string, itemId: string): string {
    const baseHrefUri = new URI(baseHref);

    const absoluteUri = baseHrefUri.is('absolute')
      ? baseHrefUri.segment(`/3/${shortCode}/items/${itemId}`)
      : this.getCurrentUri().segment([
          baseHref,
          '3',
          shortCode,
          'items',
          itemId,
        ]);

    if (absoluteUri.hasQuery('login') || absoluteUri.hasQuery('talis_login')) {
      return absoluteUri.query({ login: 1 }).suffix('html').toString();
    } else {
      return absoluteUri.query('').suffix('html').toString();
    }
  }

  /**
   * Make sure that a classic URL has lang= and also login= or talis_login= if appropriate
   * @param {URI} uri - URI to addSearch to
   * @return {URI} The modified URI
   */
  getClassicUri(uri: URI): URI {
    // Make URI use the tenant base URI
    uri = uri.absoluteTo(this.appConfig.tenant.httpsBaseUri);

    if (this.locale) {
      uri.addSearch('lang', this.locale);
    }

    if (!this.userPermissions) {
      return uri;
    }

    if (this.userPermissions.hasSuperuserRole()) {
      uri.addSearch('talis_login', 1);
    } else if (
      this.userPermissions.hasRole(this.appConfig.scopeMemberAtTenant())
    ) {
      uri.addSearch('login', 1);
    }
    return uri;
  }

  /**
   * Builds a url to a list using the concept base URI
   * @param listId - An id to a list
   * @return {URI} The list concept URI
   */
  getListConceptUri(listId: string): URI {
    return new URI(this.appConfig.tenant.conceptBaseUri).segment(
      `lists/${listId}`,
    );
  }

  /**
   * Returns a reference to the current page URI
   * NOTE: Used for testing purposes
   * @return {URI} The current page URI
   */
  getCurrentUri(): URI {
    return new URI();
  }

  /**
   * Constructs a link to the create user's profile page in TARL
   * @param listUrl - The URL of the list to redirect to after the profile has been created
   * @return {string} A URL to the 'Create Profile' page
   */
  getCreateProfileUrl(listUrl: string): string {
    const createProfileUri = new URI(this.appConfig.tenant.httpsBaseUri)
      .segment('createProfileDialogbox.html')
      .query({
        redirectUri: listUrl,
      });
    return this.getClassicUri(createProfileUri).toString();
  }

  /**
   * Performs check to see if a url is from a known TARL domain
   * @param url - URL passed in during redirect
   * @return boolean
   */
  isFromKnownDomain(url: URI): boolean {
    const httpsBaseUri = this.appConfig.tenant.httpsBaseUri;
    const apiEndpoint = this.appConfig.apiEndpoint;
    const tenantCode = this.appConfig.tenant.shortCode;
    const v3routeOrigin = new URI(apiEndpoint).segment(tenantCode).origin();

    return url.origin() === v3routeOrigin || url.origin() === httpsBaseUri;
  }

  getTarlMyBookmarksUrl(): string {
    const myListsUri = new URI(this.appConfig.tenant.httpsBaseUri).segment(
      '/mybookmarks',
    );
    return this.getClassicUri(myListsUri).toString();
  }

  getBookmarkViewUrl(userId: string, bookmarkId: string): string {
    return new URI(this.appConfig.apiEndpoint)
      .segment(this.appConfig.tenant.shortCode)
      .segment('users')
      .segment(userId)
      .segment('bookmarks')
      .segment(bookmarkId)
      .suffix('html')
      .toString();
  }

  getResourceViewUrl(resourceId: string): string {
    return new URI(this.appConfig.apiEndpoint)
      .segment(this.appConfig.tenant.shortCode)
      .segment('resources')
      .segment(resourceId)
      .suffix('html')
      .toString();
  }

  getReviewUrl(reviewId: string): string {
    return new URI(this.appConfig.tenant.reviewsUrl)
      .segment(reviewId)
      .toString();
  }

  getListViewWithAScrollToItem(listId: string, itemId: string): string {
    return new URI(this.appConfig.apiEndpoint)
      .segment(this.appConfig.tenant.shortCode)
      .segment('lists')
      .segment(listId)
      .suffix('html')
      .fragment(`item_${itemId}`)
      .toString();
  }

  getEditResourceRelativeUrl(resourceId: string): string {
    const version = '3';

    return new URI('/')
      .segment(version)
      .segment(this.appConfig.tenant.shortCode)
      .segment('resources')
      .segment(resourceId)
      .segment('edit')
      .suffix('html')
      .toString();
  }

  toRelativeUrl(url: string): string {
    return new URI(url).resource();
  }
}
